/**
 * Session, tenant and active brand.
 *
 * One provider owns all three because they change together: signing in picks an organisation,
 * an organisation owns the brands, and the active brand supplies the timezone the Now Rail ticks
 * in. Everything downstream reads `useAuth()`.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactElement, ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  AuthSession,
  Brand,
  ClientAccessRole,
  OrgSummary,
  SessionUser,
  WhitelabelConfig,
} from '@anubis-connector/shared'

import {
  apiV1,
  applySession,
  clearSession,
  onUnauthorized,
  setActiveBlogId as setApiBlogId,
  setActiveOrgId as setApiOrgId,
} from './api'
import { bootBranding, setBranding } from './branding'
import { setActiveLocale } from './format'
import { qk, resetQueryCache } from './query'

const ORG_KEY = 'pd.activeOrgId'
const BLOG_KEY = 'pd.activeBlogId'

/** Where a client auto-login link lands. Declared here because the boot check has to know it. */
export const AUTO_LOGIN_PATH = '/autoin'

const readStored = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

const writeStored = (key: string, value: string | null): void => {
  try {
    if (value === null) window.localStorage.removeItem(key)
    else window.localStorage.setItem(key, value)
  } catch {
    // Private mode or a blocked origin — the session still works, it just does not persist.
  }
}

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous'

export interface SignupInput {
  name: string
  email: string
  password: string
  orgName: string
  /**
   * The referral code from `/signup?ref=<code>`, when the visitor arrived through somebody's link.
   *
   * Optional and best-effort all the way down: the server resolves it to a referrer workspace and
   * silently drops it if it names none. A code that has been mangled in transit costs the referrer
   * their credit; it must never cost the visitor their account.
   */
  referralCode?: string
}

export interface AuthContextValue {
  status: AuthStatus
  user: SessionUser | null
  isSuperadmin: boolean

  orgs: OrgSummary[]
  activeOrg: OrgSummary | null
  switchOrg: (orgId: string) => void

  brands: Brand[]
  brandsStatus: 'pending' | 'error' | 'success'
  brandsError: unknown
  reloadBrands: () => void
  activeBrand: Brand | null
  switchBrand: (blogId: number) => void
  /** The brand's timezone, falling back to the signed-in user's. */
  activeTimezone: string

  login: (email: string, password: string) => Promise<SessionUser>
  signup: (input: SignupInput) => Promise<SessionUser>
  /** Trades a brand's client auto-login token for a session scoped to that one brand. */
  redeemAutoLogin: (token: string) => Promise<AutoLoginResult>
  /** Trades a compat caller's connect-bridge token (`/connect-bridge#<token>:<blogId>`) for a
   *  real, editor-capable session scoped to that brand. See `backend/src/compat/connectBridge.ts`
   *  for who mints it and why this is a different credential from `redeemAutoLogin`'s. */
  redeemConnectBridge: (token: string, blogId: number) => Promise<ConnectBridgeResult>
  /** The other half of the connect-bridge: mints the session *after* a real OAuth round trip,
   *  from the proof `startConnect`'s `returnTo` carried through it. See
   *  `backend/src/modules/connectBridge/controller.ts#finish`. */
  finishConnectBridge: (proof: string, exp: number, brandId: string) => Promise<void>
  logout: () => Promise<void>
  reloadSession: () => Promise<void>
}

/** What `POST /public/auto-login` answers with. The brand is named so the shell can land on it. */
export interface AutoLoginResult {
  session: AuthSession
  role: ClientAccessRole
  brandId: string
  blogId: number
  brandName: string
}

/** What `POST /public/connect-bridge/redeem` answers with. */
export interface ConnectBridgeResult {
  session: AuthSession
  blogId: number
}

const AuthContext = createContext<AuthContextValue | null>(null)

/* ── anonymous endpoints, callable without the provider ───────────────────── */

export const requestPasswordReset = (email: string): Promise<{ ok: true }> =>
  apiV1<{ ok: true }>('/auth/forgot', { method: 'POST', json: { email }, anonymous: true })

export const submitPasswordReset = (token: string, password: string): Promise<{ ok: true }> =>
  apiV1<{ ok: true }>('/auth/reset', { method: 'POST', json: { token, password }, anonymous: true })

/* ── provider ─────────────────────────────────────────────────────────────── */

export const AuthProvider = ({ children }: { children: ReactNode }): ReactElement => {
  const queryClient = useQueryClient()
  const [status, setStatus] = useState<AuthStatus>('loading')
  const [user, setUser] = useState<SessionUser | null>(null)
  const [orgId, setOrgId] = useState<string | null>(() => readStored(ORG_KEY))
  const [blogId, setBlogId] = useState<number | null>(() => {
    const raw = readStored(BLOG_KEY)
    if (raw === null) return null
    const parsed = Number.parseInt(raw, 10)
    return Number.isFinite(parsed) ? parsed : null
  })

  /** Installs a session across React state and the HTTP client in one place. */
  const adoptSession = useCallback(
    (session: AuthSession) => {
      applySession(session)
      setActiveLocale(session.user.locale)

      const stored = readStored(ORG_KEY)
      const validStored = stored && session.user.orgs.some((org) => org.id === stored) ? stored : null
      const nextOrg = validStored ?? session.user.activeOrgId
      setApiOrgId(nextOrg)
      setOrgId(nextOrg)
      writeStored(ORG_KEY, nextOrg)

      // Honour the server's last-used brand when this device has no preference of its own.
      setBlogId((previous) => {
        if (previous !== null) return previous
        if (session.user.activeBlogId === null) return null
        writeStored(BLOG_KEY, String(session.user.activeBlogId))
        return session.user.activeBlogId
      })

      setUser(session.user)
      setStatus('authenticated')
    },
    [],
  )

  const dropSession = useCallback(() => {
    clearSession()
    setUser(null)
    setStatus('anonymous')
    setBlogId(null)
    writeStored(BLOG_KEY, null)
    resetQueryCache()
  }, [])

  /* boot: the refresh cookie is httpOnly, so simply asking is the whole check */
  useEffect(() => {
    /*
     * Except on `/autoin`, which mints its own session out of a link.
     *
     * Both requests would otherwise be in flight at once and whichever answered last would win —
     * so a browser that still holds a refresh cookie from another account could land the link's
     * recipient in *that* account. The link is the credential on this path; nothing else is.
     */
    if (window.location.pathname === AUTO_LOGIN_PATH) {
      setStatus('anonymous')
      return
    }

    let cancelled = false
    void (async () => {
      try {
        const session = await apiV1<AuthSession>('/auth/refresh', {
          method: 'POST',
          anonymous: true,
        })
        if (!cancelled) adoptSession(session)
      } catch {
        if (!cancelled) setStatus('anonymous')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [adoptSession])

  /* a refresh that fails mid-session ends the session exactly once */
  useEffect(() => onUnauthorized(() => dropSession()), [dropSession])

  /* ── the workspace's own branding ───────────────────────────────────────────
     bootBranding() resolves branding from the host, which only covers partners who have
     pointed a custom domain here. Their own staff — and anyone signed in before the DNS
     lands — reach the console on the shared host, where the panel's promise ("replaces
     Anubis Connector everywhere inside this workspace") would otherwise quietly not hold.
     So once a session exists, the active workspace's branding wins over the host's. */
  useEffect(() => {
    if (status !== 'authenticated' || orgId === null) return
    let cancelled = false

    void apiV1<WhitelabelConfig>('/whitelabel')
      .then((config) => {
        if (!cancelled) setBranding(config)
      })
      .catch(() => {
        // 403 WHITELABEL_NOT_IN_PLAN is the ordinary case, not a failure: this workspace
        // is simply not white-labelled. Fall back to whatever the host resolved.
        if (!cancelled) void bootBranding()
      })

    return () => {
      cancelled = true
    }
  }, [status, orgId])

  /* ── brands for the active organisation ─────────────────────────────────── */

  const brandsQuery = useQuery({
    queryKey: qk.brands(),
    queryFn: () => apiV1<Brand[]>('/brands'),
    enabled: status === 'authenticated' && orgId !== null,
  })

  const brands = useMemo(
    () => (brandsQuery.data ?? []).filter((brand) => !brand.archived),
    [brandsQuery.data],
  )

  const activeBrand = useMemo(() => {
    if (brands.length === 0) return null
    return brands.find((brand) => brand.blogId === blogId) ?? brands[0] ?? null
  }, [brands, blogId])

  /* keep the compat client's blogId in step with the picker */
  useEffect(() => {
    setApiBlogId(activeBrand?.blogId ?? null)
  }, [activeBrand])

  const switchBrand = useCallback((nextBlogId: number) => {
    setBlogId(nextBlogId)
    setApiBlogId(nextBlogId)
    writeStored(BLOG_KEY, String(nextBlogId))
  }, [])

  const switchOrg = useCallback(
    (nextOrgId: string) => {
      if (nextOrgId === orgId) return
      setApiOrgId(nextOrgId)
      setOrgId(nextOrgId)
      writeStored(ORG_KEY, nextOrgId)
      setBlogId(null)
      writeStored(BLOG_KEY, null)
      // Everything cached belongs to the previous tenant.
      resetQueryCache()
    },
    [orgId],
  )

  /* ── actions ────────────────────────────────────────────────────────────── */

  const login = useCallback(
    async (email: string, password: string): Promise<SessionUser> => {
      const session = await apiV1<AuthSession>('/auth/login', {
        method: 'POST',
        json: { email, password },
        anonymous: true,
      })
      resetQueryCache()
      adoptSession(session)
      return session.user
    },
    [adoptSession],
  )

  const signup = useCallback(
    async (input: SignupInput): Promise<SessionUser> => {
      const session = await apiV1<AuthSession>('/auth/register', {
        method: 'POST',
        json: input,
        anonymous: true,
      })
      resetQueryCache()
      adoptSession(session)
      return session.user
    },
    [adoptSession],
  )

  /**
   * Redeem a brand's client auto-login link.
   *
   * Unlike `login`, this forces the brand: the device's remembered `activeBlogId` is cleared
   * *before* the session is adopted, because a link opens one brand and honouring a leftover
   * preference would land the recipient on a picker they have no access to. The cache is reset
   * first for the same reason it is on sign-in — whatever is in it belongs to somebody else.
   */
  const redeemAutoLogin = useCallback(
    async (token: string): Promise<AutoLoginResult> => {
      const result = await apiV1<AutoLoginResult>('/public/auto-login', {
        method: 'POST',
        json: { token },
        anonymous: true,
      })
      resetQueryCache()
      setBlogId(null)
      writeStored(BLOG_KEY, null)
      writeStored(ORG_KEY, null)
      adoptSession(result.session)
      setBlogId(result.blogId)
      setApiBlogId(result.blogId)
      writeStored(BLOG_KEY, String(result.blogId))
      return result
    },
    [adoptSession],
  )

  /**
   * Redeem a compat caller's connect-bridge link.
   *
   * Same "force the brand" reasoning as `redeemAutoLogin` just above: this device may already
   * hold a stored org/brand preference from the signed-in person's own ordinary use, and
   * `adoptSession` honours that preference when one exists — exactly wrong for a link whose
   * entire point is landing on one specific brand. Clearing it first is what makes that brand
   * win regardless of what was there before.
   */
  const redeemConnectBridge = useCallback(
    async (token: string, blogId: number): Promise<ConnectBridgeResult> => {
      const result = await apiV1<ConnectBridgeResult>('/public/connect-bridge/redeem', {
        method: 'POST',
        json: { token, blogId },
        anonymous: true,
      })
      resetQueryCache()
      setBlogId(null)
      writeStored(BLOG_KEY, null)
      writeStored(ORG_KEY, null)
      adoptSession(result.session)
      setBlogId(result.blogId)
      setApiBlogId(result.blogId)
      writeStored(BLOG_KEY, String(result.blogId))
      return result
    },
    [adoptSession],
  )

  /** See `finishConnectBridge`'s own doc comment on `AuthContextValue`. Unlike the other two
   *  redeems, this one does not force a brand: the page that called it already knows exactly
   *  which `brandId` to show (it is in its own URL), so the ordinary "honour what is stored"
   *  behaviour `adoptSession` already has is the right one here, not a reason to override it. */
  const finishConnectBridge = useCallback(
    async (proof: string, exp: number, brandId: string): Promise<void> => {
      const result = await apiV1<{ session: AuthSession }>('/public/connect-bridge/finish', {
        method: 'POST',
        json: { proof, exp, brandId },
        anonymous: true,
      })
      adoptSession(result.session)
    },
    [adoptSession],
  )

  const logout = useCallback(async (): Promise<void> => {
    try {
      await apiV1<{ ok: true }>('/auth/logout', { method: 'POST' })
    } catch {
      // The cookie is gone either way; the local session must still end.
    }
    dropSession()
  }, [dropSession])

  const reloadSession = useCallback(async (): Promise<void> => {
    const fresh = await apiV1<SessionUser>('/auth/me')
    setUser(fresh)
    setActiveLocale(fresh.locale)
    void queryClient.invalidateQueries({ queryKey: qk.brands() })
  }, [queryClient])

  const orgs = useMemo(() => user?.orgs ?? [], [user])
  const activeOrg = useMemo(
    () => orgs.find((org) => org.id === orgId) ?? orgs[0] ?? null,
    [orgs, orgId],
  )

  const { status: brandsStatus, error: brandsError, refetch: refetchBrands } = brandsQuery
  const reloadBrands = useCallback(() => {
    void refetchBrands()
  }, [refetchBrands])

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      isSuperadmin: user?.isSuperadmin ?? false,
      orgs,
      activeOrg,
      switchOrg,
      brands,
      brandsStatus,
      brandsError,
      reloadBrands,
      activeBrand,
      switchBrand,
      activeTimezone: activeBrand?.timezone ?? user?.timezone ?? 'UTC',
      login,
      signup,
      redeemAutoLogin,
      redeemConnectBridge,
      finishConnectBridge,
      logout,
      reloadSession,
    }),
    [
      status,
      user,
      orgs,
      activeOrg,
      switchOrg,
      brands,
      brandsStatus,
      brandsError,
      reloadBrands,
      activeBrand,
      switchBrand,
      login,
      signup,
      redeemAutoLogin,
      redeemConnectBridge,
      finishConnectBridge,
      logout,
      reloadSession,
    ],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>.')
  return ctx
}

/** Narrow helper for screens that are already behind ProtectedRoute. */
export const useSessionUser = (): SessionUser => {
  const { user } = useAuth()
  if (!user) throw new Error('useSessionUser requires an authenticated session.')
  return user
}
